import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Tooltip as MuiTooltip,
  Snackbar,
  Grid,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConstructionIcon from '@mui/icons-material/Construction';
import BuildIcon from '@mui/icons-material/Build';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  Cell
} from 'recharts';

// Authentication imports
import { useAuth } from '../../hooks/auth/useAuth';
import api from '../../services/api';

// Import DefectViewerModal
import DefectViewerModal from '../MapView/modals/DefectViewerModal';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

// Status configuration for styling and icons - matching asset_status_enum values and card colors
const statusConfig = {
  'under_construction': { 
    color: '#ff7043', 
    backgroundColor: alpha('#ff7043', 0.1), 
    icon: <ConstructionIcon fontSize="small" /> 
  },
  'under_renovation': { 
    color: '#9c27b0', 
    backgroundColor: alpha('#9c27b0', 0.1), 
    icon: <BuildIcon fontSize="small" /> 
  },
  'active': { 
    color: '#4caf50', 
    backgroundColor: alpha('#4caf50', 0.1), 
    icon: <CheckCircleIcon fontSize="small" /> 
  },
  'maintenance': { 
    color: '#2196f3', 
    backgroundColor: alpha('#2196f3', 0.1), 
    icon: <EngineeringIcon fontSize="small" /> 
  },
  'inactive': { 
    color: '#607d8b', 
    backgroundColor: alpha('#607d8b', 0.1), 
    icon: <PauseCircleIcon fontSize="small" /> 
  },
  'decommissioned': { 
    color: '#f44336', 
    backgroundColor: alpha('#f44336', 0.1), 
    icon: <CancelIcon fontSize="small" /> 
  }
};

const SlopeAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, getUserOrganization } = useAuth();
  
  // Get organization info from auth context
  const userOrganization = getUserOrganization();
  const organizationName = userOrganization?.name || '';
  const organizationId = userOrganization?.id;

  // State for defects chart data
  const [slopeData, setSlopeData] = useState([]);
  const [severityLevels, setSeverityLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for section assets chart data
  const [sectionData, setSectionData] = useState([]);
  const [ratingLevels, setRatingLevels] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionError, setSectionError] = useState(null);

  // State for slope subtypes chart data
  const [subtypeData, setSubtypeData] = useState([]);
  const [subtypeLoading, setSubtypeLoading] = useState(false);
  const [subtypeError, setSubtypeError] = useState(null);

  // State for details table
  const [selectedSlope, setSelectedSlope] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [slopeDetails, setSlopeDetails] = useState(null);
  const [sectionDetails, setSectionDetails] = useState(null);
  const [subtypeDetails, setSubtypeDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsType, setDetailsType] = useState(null); // 'slope', 'section', or 'subtype'

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Snackbar state for user notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // DefectViewerModal state
  const [defectViewerOpen, setDefectViewerOpen] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);

  // Year selector state
  const [selectedYear, setSelectedYear] = useState('all-time');
  const [availableYears, setAvailableYears] = useState([]);
  const [yearsLoading, setYearsLoading] = useState(false);

  // Fetch available years for year selector
  const fetchAvailableYears = useCallback(async () => {
    if (!organizationId) return;

    setYearsLoading(true);
    
    try {
      const response = await api.get('/analytics/available-years');
      
      if (response.data.success) {
        setAvailableYears(response.data.data.years || []);
      } else {
        console.error('Failed to fetch available years:', response.data.error);
      }
    } catch (err) {
      console.error('Failed to fetch available years:', err);
    } finally {
      setYearsLoading(false);
    }
  }, [organizationId]);

  // Fetch slope defects analytics data
  const fetchSlopeAnalytics = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);
    
    try {
      const params = selectedYear !== 'all-time' ? { year: selectedYear } : {};
      const response = await api.get('/analytics/slope-defects', { params });
      
      if (response.data.success) {
        const { slopes, severity_levels } = response.data.data;
        
        // Transform data for stacked bar chart
        const chartData = slopes.map(slope => {
          
          const chartSlope = {
            slope_id: slope.slope_id,
            slope_name: slope.slope_name.length > 20 
              ? `${slope.slope_name.substring(0, 20)}...` 
              : slope.slope_name,
            slope_name_full: slope.slope_name,
            slope_code: slope.slope_code,
            total_defects: slope.total_defects,
            rating_level_id: slope.rating_level_id || null,
            rating_level_value: slope.rating_level_value || null,
            rating_level_name: slope.rating_level_name || null,
            rating_color_code: slope.rating_color_code || null
          };
          
          // Add severity breakdown as individual properties for stacked bars
          slope.severity_breakdown.forEach(severity => {
            chartSlope[`severity_${severity.severity_level_id}`] = severity.defect_count;
          });
          
          // Add inspection details for tooltip
          if (slope.inspection_details) {
            chartSlope.inspection_details = slope.inspection_details;
          }
          
          return chartSlope;
        });
        
        setSlopeData(chartData);
        setSeverityLevels(severity_levels);
      } else {
        setError(response.data.error || 'Failed to fetch slope analytics');
      }
    } catch (err) {
      console.error('Failed to fetch slope analytics:', err);
      setError('Failed to load slope analytics data');
    } finally {
      setLoading(false);
    }
  }, [organizationId, selectedYear]);

  // Fetch slope defect details
  const fetchSlopeDetails = useCallback(async (slopeId, severityLevelId = null) => {
    if (!slopeId) return;

    setDetailsLoading(true);
    setDetailsError(null);
    
    try {
      const params = { slope_id: slopeId };
      if (severityLevelId) {
        params.severity_level_id = severityLevelId;
      }
      // Include year filter for consistency with chart data
      if (selectedYear !== 'all-time') {
        params.year = selectedYear;
      }
      
      const response = await api.get('/analytics/slope-defects-details', { params });
      
      if (response.data.success) {
        setSlopeDetails(response.data.data);
        setShowDetails(true);
        setPage(0); // Reset pagination
      } else {
        setDetailsError(response.data.error || 'Failed to fetch slope details');
      }
    } catch (err) {
      console.error('Failed to fetch slope details:', err);
      setDetailsError('Failed to load slope details');
    } finally {
      setDetailsLoading(false);
    }
  }, [selectedYear]);

  // Fetch slope assets by section analytics data
  const fetchSectionAnalytics = useCallback(async () => {
    if (!organizationId) return;

    setSectionLoading(true);
    setSectionError(null);
    
    try {
      const params = selectedYear !== 'all-time' ? { year: selectedYear } : {};
      const response = await api.get('/analytics/slope-assets-by-section', { params });
      
      if (response.data.success) {
        const { sections, rating_levels, networks } = response.data.data;
        
        // Create network color lookup map
        const networkColorMap = {};
        networks.forEach(network => {
          networkColorMap[network.id] = network.color_code;
        });
        
        // Transform data for stacked bar chart
        const chartData = sections.map(section => {
          const chartSection = {
            section_id: section.section_id,
            section_name: section.section_name.length > 15 
              ? `${section.section_name.substring(0, 15)}...` 
              : section.section_name,
            section_name_full: section.section_name,
            network_id: section.network_id,
            network_name: section.network_name,
            network_color: networkColorMap[section.network_id] || '#2196F3',
            total_slopes: section.total_slopes
          };
          
          // Add rating breakdown as individual properties for stacked bars
          section.rating_breakdown.forEach(rating => {
            const ratingKey = rating.rating_level_id ? `rating_${rating.rating_level_id}` : 'rating_na';
            chartSection[ratingKey] = rating.slope_count;
          });
          
          return chartSection;
        });
        
        setSectionData(chartData);
        setRatingLevels(rating_levels);
        setNetworks(networks);
      } else {
        setSectionError(response.data.error || 'Failed to fetch section analytics');
      }
    } catch (err) {
      console.error('Failed to fetch section analytics:', err);
      setSectionError('Failed to load section analytics data');
    } finally {
      setSectionLoading(false);
    }
  }, [organizationId, selectedYear]);

  // Fetch slope assets by subtype analytics data
  const fetchSubtypeAnalytics = useCallback(async () => {
    if (!organizationId) return;

    setSubtypeLoading(true);
    setSubtypeError(null);
    
    try {
      const response = await api.get('/analytics/slope-assets-by-subtype');
      
      if (response.data.success) {
        const { subtypes } = response.data.data;
        
        // Transform data for simple bar chart
        const chartData = subtypes.map(subtype => ({
          subtype_id: subtype.subtype_id,
          subtype_name: subtype.subtype_name.length > 20 
            ? `${subtype.subtype_name.substring(0, 20)}...` 
            : subtype.subtype_name,
          subtype_name_full: subtype.subtype_name,
          subtype_code: subtype.subtype_code,
          slope_count: parseInt(subtype.slope_count)
        }));
        
        setSubtypeData(chartData);
      } else {
        setSubtypeError(response.data.error || 'Failed to fetch subtype analytics');
      }
    } catch (err) {
      console.error('Failed to fetch subtype analytics:', err);
      setSubtypeError('Failed to load subtype analytics data');
    } finally {
      setSubtypeLoading(false);
    }
  }, [organizationId]);

  // Fetch section slope details
  const fetchSectionDetails = useCallback(async (sectionId, ratingLevelId = null) => {
    if (!sectionId) return;

    setDetailsLoading(true);
    setDetailsError(null);
    
    try {
      const params = { section_id: sectionId };
      if (ratingLevelId) {
        params.rating_level_id = ratingLevelId;
      }
      // Include year filter for consistency with chart data
      if (selectedYear !== 'all-time') {
        params.year = selectedYear;
      }
      
      const response = await api.get('/analytics/slope-assets-section-details', { params });
      
      if (response.data.success) {
        setSectionDetails(response.data.data);
        setDetailsType('section');
        setShowDetails(true);
        setPage(0); // Reset pagination
      } else {
        setDetailsError(response.data.error || 'Failed to fetch section details');
      }
    } catch (err) {
      console.error('Failed to fetch section details:', err);
      setDetailsError('Failed to load section details');
    } finally {
      setDetailsLoading(false);
    }
  }, [selectedYear]);

  // Fetch subtype slope details
  const fetchSubtypeDetails = useCallback(async (subtypeId) => {
    if (!subtypeId) return;

    setDetailsLoading(true);
    setDetailsError(null);
    
    try {
      const params = { subtype_id: subtypeId };
      
      const response = await api.get('/analytics/slope-assets-subtype-details', { params });
      
      if (response.data.success) {
        setSubtypeDetails(response.data.data);
        setDetailsType('subtype');
        setShowDetails(true);
        setPage(0); // Reset pagination
      } else {
        setDetailsError(response.data.error || 'Failed to fetch subtype details');
      }
    } catch (err) {
      console.error('Failed to fetch subtype details:', err);
      setDetailsError('Failed to load subtype details');
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // Load available years on component mount
  useEffect(() => {
    fetchAvailableYears();
  }, [fetchAvailableYears]);

  // Load data when component mounts or year changes
  useEffect(() => {
    fetchSlopeAnalytics();
    fetchSectionAnalytics();
    fetchSubtypeAnalytics();
  }, [fetchSlopeAnalytics, fetchSectionAnalytics, fetchSubtypeAnalytics]);

  // Handle year selection change
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  // Handle slope defects chart bar click
  const handleSlopeBarClick = (data, index) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const clickedData = data.activePayload[0].payload;
      const slopeId = clickedData.slope_id;
      
      setSelectedSlope({
        id: slopeId,
        name: clickedData.slope_name_full,
        code: clickedData.slope_code,
        total_defects: clickedData.total_defects,
        rating_level_id: clickedData.rating_level_id || null,
        rating_level_value: clickedData.rating_level_value || null,
        rating_level_name: clickedData.rating_level_name || null,
        rating_color_code: clickedData.rating_color_code || null
      });
      
      setSelectedSection(null); // Clear section selection
      setDetailsType('slope');
      
      // Call without severity filtering to show all defects
      fetchSlopeDetails(slopeId, null);
    }
  };

  // Handle section assets chart bar click
  const handleSectionBarClick = (data, index) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const clickedData = data.activePayload[0].payload;
      const sectionId = clickedData.section_id;
      
      setSelectedSection({
        id: sectionId,
        name: clickedData.section_name_full,
        network_name: clickedData.network_name,
        total_slopes: clickedData.total_slopes
      });
      
      setSelectedSlope(null); // Clear slope selection
      setDetailsType('section');
      
      // Call without rating filtering to show all slopes
      fetchSectionDetails(sectionId, null);
    }
  };

  // Handle subtype chart bar click
  const handleSubtypeBarClick = (data, index) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const clickedData = data.activePayload[0].payload;
      const subtypeId = clickedData.subtype_id;
      
      setSelectedSubtype({
        id: subtypeId,
        name: clickedData.subtype_name_full,
        code: clickedData.subtype_code,
        slope_count: clickedData.slope_count
      });
      
      setSelectedSlope(null); // Clear slope selection
      setSelectedSection(null); // Clear section selection
      setDetailsType('subtype');
      
      // Fetch slopes for this subtype
      fetchSubtypeDetails(subtypeId);
    }
  };

  // Handle View on Map action for slope assets
  const handleViewOnMap = (slope, event) => {
    event.stopPropagation(); // Prevent any row click events
    
    // Transform slope data to match the expected asset structure for Map View
    const assetData = {
      id: slope.id,
      name: slope.name,
      code: slope.code,
      status: slope.status,
      center_lat: slope.center_lat,
      center_lng: slope.center_lng,
      // Add additional fields that Map View might expect
      subtype_name: slope.slope_subtype || 'Slope',
      type_name: 'Slope',
      category_name: 'Infrastructure',
      rating_name: slope.rating_level_name,
      rating_color_code: slope.rating_color,
      created_at: slope.created_at
    };

    console.log('🗺️ [SlopeAnalytics] Navigating to Map View with slope data:', assetData);
    
    // Check if coordinates are available
    if (!assetData.center_lat || !assetData.center_lng) {
      console.warn('⚠️ [SlopeAnalytics] Slope missing coordinates:', {
        id: slope.id,
        name: slope.name,
        center_lat: slope.center_lat,
        center_lng: slope.center_lng
      });
      
      // Show user-friendly message for missing coordinates
      setSnackbarMessage(`Unable to show "${slope.name}" on map: Slope location coordinates are not available in the database.`);
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    navigate('/map-view', { state: { selectedAsset: assetData, source: 'assetsPage' } });
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Handle view defect
  const handleViewDefect = (defect) => {
    console.log('🔍 [SlopeAnalytics] Opening defect viewer for:', defect);
    setSelectedDefect(defect);
    setDefectViewerOpen(true);
  };

  // Handle defect viewer close
  const handleDefectViewerClose = () => {
    setDefectViewerOpen(false);
    setSelectedDefect(null);
  };

  // Handle defect viewer error
  const handleDefectViewerError = (errorMessage) => {
    setSnackbarMessage(errorMessage);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  // Helper to normalize rating names from CxN → RxN (or C5 → R5)
  const toRx = (name) => {
    if (!name) return name;
    const str = String(name);
    return str.replace(/Cx/gi, 'Rx').replace(/C(?=\d)/gi, 'R');
  };

  // Custom label function for rating level at top of bars
  const RatingLabel = (props) => {
    const { x, y, width, height, payload, index } = props;
    
    // Get the rating from the slope data directly using the index
    const slope = slopeData[index];
    const ratingName = toRx(slope?.rating_level_name || 'N/A');
    const ratingColor = slope?.rating_color_code || theme.palette.text.secondary;
    
    return (
      <text 
        x={x + width / 2} 
        y={y - 8} 
        fill={ratingColor}
        textAnchor="middle" 
        fontSize="12" 
        fontWeight="600"
        style={{ userSelect: 'none' }}
      >
        {ratingName}
      </text>
    );
  };

  // Custom tooltip for the slope defects chart
  const SlopeCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      const totalDefects = data.total_defects;
      const inspectionDetails = data.inspection_details;
      
      return (
        <Paper sx={{ p: 2, boxShadow: theme.shadows[8], maxWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.slope_name_full}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Code: {data.slope_code}
          </Typography>
          
          {/* Rating Level */}
          <Typography variant="body2" color={data.rating_level_value ? "primary" : "text.secondary"} sx={{ mb: 1, fontWeight: 500 }}>
            🏗️ Rating: {data.rating_level_value || 'N/A'} {data.rating_level_name && data.rating_level_value && `(${toRx(data.rating_level_name)})`}
            {selectedYear !== 'all-time' && (
              <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                ({selectedYear} rating)
              </Typography>
            )}
          </Typography>
          
          {/* Inspection Details */}
          {inspectionDetails && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: theme.palette.grey[50], borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                Latest Inspection
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                📅 Date: {inspectionDetails.inspection_date ? new Date(inspectionDetails.inspection_date).toLocaleDateString() : 'N/A'}
              </Typography>
              {inspectionDetails.cycle && (
                <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  🔄 Cycle: {inspectionDetails.cycle}
                </Typography>
              )}
              {inspectionDetails.inspection_type && (
                <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  📋 Type: {inspectionDetails.inspection_type}
                </Typography>
              )}
              {inspectionDetails.inspector_name && (
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  👤 Inspector: {inspectionDetails.inspector_name}
                </Typography>
              )}
            </Box>
          )}
          
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Total Defects: {totalDefects}
          </Typography>
          {payload.map((entry, index) => {
            if (entry.value > 0) {
              const severityId = entry.dataKey.replace('severity_', '');
              const severity = severityLevels.find(s => s.id.toString() === severityId);
              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      backgroundColor: severity?.color_code || entry.color,
                      mr: 1,
                      borderRadius: 1
                    }} 
                  />
                  <Typography variant="body2">
                    {severity?.name || 'Unknown'}: {entry.value}
                  </Typography>
                </Box>
              );
            }
            return null;
          })}
        </Paper>
      );
    }
    return null;
  };

  // Custom tooltip for the section assets chart
  const SectionCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      const totalSlopes = data.total_slopes;
      
      return (
        <Paper sx={{ p: 2, boxShadow: theme.shadows[8], maxWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.section_name_full}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Network: {data.network_name}
          </Typography>
          
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Total Slope Assets: {totalSlopes}
          </Typography>
          {payload.map((entry, index) => {
            if (entry.value > 0) {
              const ratingKey = entry.dataKey;
              const isNA = ratingKey === 'rating_na';
              const ratingId = isNA ? null : ratingKey.replace('rating_', '');
              const rating = isNA ? { name: 'N/A', color_code: '#9E9E9E' } : ratingLevels.find(r => r.id?.toString() === ratingId);
              
              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      backgroundColor: rating?.color_code || entry.color,
                      mr: 1,
                      borderRadius: 1
                    }} 
                  />
                  <Typography variant="body2">
                    {toRx(rating?.name) || 'Unknown'}: {entry.value} slope{entry.value !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              );
            }
            return null;
          })}
        </Paper>
      );
    }
    return null;
  };

  // Custom tooltip for the subtype chart
  const SubtypeCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      
      return (
        <Paper sx={{ p: 2, boxShadow: theme.shadows[8], maxWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.subtype_name_full}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Code: {data.subtype_code}
          </Typography>
          
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Slope Count: {data.slope_count}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedDefects = slopeDetails?.defects ? 
    slopeDetails.defects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [];
  
  const paginatedSlopes = sectionDetails?.slopes ? 
    sectionDetails.slopes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [];

  const paginatedSubtypeSlopes = subtypeDetails?.slopes ? 
    subtypeDetails.slopes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [];

  return (
    <DisciplineAccessControl requiredDiscipline="Geotechnical">
    <Box>
      {/* Year Selector */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          mb: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Slope Analytics Filter
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filter all slope analytics by year
            </Typography>
          </Box>
          
          <Box sx={{ flex: 1, minWidth: 250, maxWidth: 400 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Year Filter</InputLabel>
              <Select
                value={selectedYear}
                label="Year Filter"
                onChange={handleYearChange}
                disabled={yearsLoading}
                sx={{
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  }
                }}
              >
                <MenuItem value="all-time">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All Time Data
                    </Typography>
                  </Box>
                </MenuItem>
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {year}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Year {year} data
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedYear !== 'all-time' && (
            <Chip
              label={`Year ${selectedYear}`}
              color="primary"
              variant="filled"
              onDelete={() => setSelectedYear('all-time')}
              sx={{ fontWeight: 500 }}
            />
          )}

          {yearsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading years...
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Slope Defects Chart */}
      <Paper 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          mb: 3
        }}
      >
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Top Slopes by Defect Count 
          {selectedYear === 'all-time' ? ' (Latest Inspection)' : ` (${selectedYear} Inspections)`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click on a bar to view detailed defects for that slope
          {selectedYear !== 'all-time' && ` • Showing data from ${selectedYear}`}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : slopeData.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No slope defect data found for your organization
          </Alert>
        ) : (
          <>
            <Box sx={{ height: 400, width: '100%', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={slopeData}
                  margin={{
                    top: 40,
                    right: 30,
                    left: 20,
                    bottom: 60
                  }}
                  onClick={handleSlopeBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="slope_name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={12}
                  />
                  <YAxis 
                    tickFormatter={(value) => Math.round(value).toString()}
                    allowDecimals={false}
                  />
                  <Tooltip content={<SlopeCustomTooltip />} />
                  
                  {/* Render bars for each severity level */}
                  {severityLevels
                    .sort((a, b) => a.value - b.value) // Sort by severity value ascending (lowest to highest)
                    .map((severity, index) => (
                      <Bar
                        key={severity.id}
                        dataKey={`severity_${severity.id}`}
                        stackId="defects"
                        fill={severity.color_code || theme.palette.primary.main}
                        name={severity.name}
                        cursor="pointer"
                      >
                        {/* Add rating level labels only to the topmost bar (last in ascending order) */}
                        {index === severityLevels.length - 1 && (
                          <LabelList content={RatingLabel} position="top" />
                        )}
                      </Bar>
                    ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>


          </>
        )}
      </Paper>

      {/* Slope Assets by Section Chart + 3rd Section Row */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
        gap: 3,
        mb: 3
      }}>
        {/* Slope Assets by Section Chart - Half Width */}
        <Paper 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            boxShadow: theme.shadows[1]
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Slope Assets by Section
            {selectedYear !== 'all-time' && ` (${selectedYear} Conditions)`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Click on a bar to view detailed slope listing for that section. Sections are color-coded by network.
            {selectedYear !== 'all-time' && ` • Showing rating conditions from ${selectedYear}`}
          </Typography>

          {sectionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sectionError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sectionError}
            </Alert>
          ) : sectionData.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No slope asset data found for your organization sections
            </Alert>
          ) : (
            <Box sx={{ height: 350, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={sectionData}
                  margin={{
                    top: 20,
                    right: 15,
                    left: 15,
                    bottom: 40
                  }}
                  maxBarSize={sectionData.length <= 5 ? 40 : sectionData.length <= 10 ? 30 : 25}
                  barCategoryGap={sectionData.length <= 5 ? "15%" : sectionData.length <= 10 ? "10%" : "5%"}
                  onClick={handleSectionBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="section_name" 
                    angle={-45}
                    textAnchor="end"
                    height={90}
                    fontSize={sectionData.length > 10 ? 9 : 11}
                    tick={(props) => {
                      const { payload, x, y } = props;
                      const section = sectionData.find(s => s.section_name === payload.value);
                      const networkColor = section?.network_color || '#2196F3';
                      
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text 
                            x={0} 
                            y={0} 
                            dy={16} 
                            textAnchor="end" 
                            fill={networkColor}
                            fontSize={sectionData.length > 10 ? "9" : "11"}
                            fontWeight="500"
                            transform="rotate(-45)"
                          >
                            {payload.value}
                          </text>
                        </g>
                      );
                    }}
                  />
                  <YAxis 
                    tickFormatter={(value) => Math.round(value).toString()}
                    allowDecimals={false}
                    width={35}
                  />
                  <Tooltip content={<SectionCustomTooltip />} />
                  
                  {/* Render bars for each rating level */}
                  {ratingLevels
                    .sort((a, b) => (a.value || 0) - (b.value || 0)) // Sort by rating value ascending (lowest to highest)
                    .map((rating, index) => {
                      const dataKey = rating.id ? `rating_${rating.id}` : 'rating_na';
                      return (
                        <Bar
                          key={rating.id || 'na'}
                          dataKey={dataKey}
                          stackId="slopes"
                          fill={rating.color_code || '#9E9E9E'}
                          name={rating.name}
                          cursor="pointer"
                          radius={[2, 2, 0, 0]}
                        />
                      );
                    })}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Paper>

        {/* Slope Assets by Subtype Chart - Half Width */}
        <Paper 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            boxShadow: theme.shadows[1]
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Slope Assets by Subtype
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Distribution of slope assets across different slope subtypes in your organization.
          </Typography>

          {subtypeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : subtypeError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {subtypeError}
            </Alert>
          ) : subtypeData.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No slope subtype data found for your organization
            </Alert>
          ) : (
            <Box sx={{ height: 350, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={subtypeData}
                  margin={{
                    top: 20,
                    right: 15,
                    left: 15,
                    bottom: 40
                  }}
                  maxBarSize={subtypeData.length <= 5 ? 50 : subtypeData.length <= 10 ? 40 : 30}
                  barCategoryGap={subtypeData.length <= 5 ? "20%" : subtypeData.length <= 10 ? "15%" : "10%"}
                  onClick={handleSubtypeBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="subtype_name" 
                    angle={-45}
                    textAnchor="end"
                    height={90}
                    fontSize={subtypeData.length > 8 ? 9 : 11}
                  />
                  <YAxis 
                    tickFormatter={(value) => Math.round(value).toString()}
                    allowDecimals={false}
                    width={35}
                  />
                  <Tooltip content={<SubtypeCustomTooltip />} />
                  
                  <Bar
                    dataKey="slope_count"
                    fill={theme.palette.primary.main}
                    name="Slope Count"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                  >
                    {/* Add different colors for each bar */}
                    {subtypeData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={`hsl(${(index * 360) / subtypeData.length}, 70%, 50%)`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Details Table */}
      <Collapse in={showDetails && (!!slopeDetails || !!sectionDetails || !!subtypeDetails)}>
        <Paper 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            boxShadow: theme.shadows[1]
          }}
        >
          {/* Slope Details Header */}
          {selectedSlope && detailsType === 'slope' && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Slope Details: {selectedSlope.name}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`Code: ${selectedSlope.code}`} variant="outlined" />
                <Chip 
                  label={`Total Defects: ${selectedSlope.total_defects}`} 
                  color="primary" 
                />
                <Chip 
                  label={`Rating: ${selectedSlope.rating_level_value || 'N/A'}${selectedSlope.rating_level_name && selectedSlope.rating_level_value ? ` (${toRx(selectedSlope.rating_level_name)})` : ''}`}
                  color={selectedSlope.rating_level_value ? "secondary" : "default"}
                  variant="outlined"
                />
                {slopeDetails?.slope_info?.latest_inspection_date && (
                  <Chip 
                    label={`Latest Inspection: ${new Date(slopeDetails.slope_info.latest_inspection_date).toLocaleDateString()}`}
                    variant="outlined"
                  />
                )}
              </Stack>
            </>
          )}

          {/* Section Details Header */}
          {selectedSection && detailsType === 'section' && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Section Details: {selectedSection.name}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`Network: ${selectedSection.network_name}`} variant="outlined" />
                <Chip 
                  label={`Total Slopes: ${selectedSection.total_slopes}`} 
                  color="primary" 
                />
              </Stack>
            </>
          )}

          {/* Subtype Details Header */}
          {selectedSubtype && detailsType === 'subtype' && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Slope Subtype Details: {selectedSubtype.name}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`Code: ${selectedSubtype.code}`} variant="outlined" />
                <Chip 
                  label={`Total Slopes: ${selectedSubtype.slope_count}`} 
                  color="primary" 
                />
                {subtypeDetails?.subtype_info?.type_name && (
                  <Chip 
                    label={`Type: ${subtypeDetails.subtype_info.type_name}`} 
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Stack>
            </>
          )}

          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : detailsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailsError}
            </Alert>
          ) : (
            <>
              {/* Slope Defects Table */}
              {detailsType === 'slope' && slopeDetails?.defects && (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Defect Type</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Detection Time</TableCell>
                      <TableCell>Inspector</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDefects.map((defect) => (
                      <TableRow key={defect.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {defect.defect_type_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={defect.severity_name}
                            size="small"
                            sx={{
                              backgroundColor: defect.color_code || theme.palette.grey[200],
                              color: theme.palette.getContrastText(defect.color_code || theme.palette.grey[200]),
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={defect.status}
                            size="small"
                            variant="outlined"
                            color={defect.status === 'resolved' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(defect.detection_time).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {defect.inspector_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {defect.notes || 'No notes'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <MuiTooltip title="View defect photo with annotations">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDefect(defect)}
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </MuiTooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {slopeDetails.defects.length > rowsPerPage && (
                <TablePagination
                  component="div"
                  count={slopeDetails.defects.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              )}
            </>
              )}

              {/* Section Slope Assets Table */}
              {detailsType === 'section' && sectionDetails?.slopes && (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Slope Name</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell>
                            Hazard & Risk Ranking
                            {selectedYear !== 'all-time' && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                ({selectedYear})
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Slope Type</TableCell>
                          <TableCell>Created Date</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedSlopes.map((slope) => (
                          <TableRow key={slope.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {slope.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {slope.code}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={toRx(slope.rating_level_name) || 'N/A'}
                                variant="outlined"
                                sx={{ 
                                  mr: 0.5,
                                  borderColor: slope.rating_color || theme.palette.grey[400],
                                  color: slope.rating_color || theme.palette.grey[600]
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={slope.status}
                                color="default"
                                variant="outlined"
                                sx={{ mr: 0.5 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {slope.slope_subtype || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(slope.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <MuiTooltip title="View on Map">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleViewOnMap(slope, e)}
                                  sx={{ color: theme.palette.primary.main }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </MuiTooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {sectionDetails.slopes.length > rowsPerPage && (
                    <TablePagination
                      component="div"
                      count={sectionDetails.slopes.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  )}
                </>
              )}

              {/* Subtype Slope Assets Table */}
              {detailsType === 'subtype' && subtypeDetails?.slopes && (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Slope Name</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell>
                            Hazard & Risk Ranking
                            {selectedYear !== 'all-time' && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                (current)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Section</TableCell>
                          <TableCell>Network</TableCell>
                          <TableCell>Created Date</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedSubtypeSlopes.map((slope) => (
                          <TableRow key={slope.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {slope.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {slope.code}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={toRx(slope.rating_level_name) || 'N/A'}
                                variant="outlined"
                                sx={{ 
                                  mr: 0.5,
                                  borderColor: slope.rating_color || theme.palette.grey[400],
                                  color: slope.rating_color || theme.palette.grey[600]
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={slope.status}
                                color="default"
                                variant="outlined"
                                sx={{ mr: 0.5 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {slope.section_name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {slope.network_name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(slope.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <MuiTooltip title="View on Map">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleViewOnMap(slope, e)}
                                  sx={{ color: theme.palette.primary.main }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </MuiTooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {subtypeDetails.slopes.length > rowsPerPage && (
                    <TablePagination
                      component="div"
                      count={subtypeDetails.slopes.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      </Collapse>

      {/* Snackbar for user notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* DefectViewerModal */}
      <DefectViewerModal
        open={defectViewerOpen}
        onClose={handleDefectViewerClose}
        defect={selectedDefect}
        onError={handleDefectViewerError}
      />
    </Box>
    </DisciplineAccessControl>
  );
};

export default SlopeAnalytics;